import axios from 'axios';
import authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : 'http://localhost:5000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Adjunta el access_token de Authentik en cada request
apiClient.interceptors.request.use(
  async (config) => {
    const token = await authService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 401 = token inválido/expirado → re-autenticar
// 403 = autenticado pero sin acceso → propagar para que el caller decida (no re-login, evita loop)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      await authService.login();
    }
    console.error('API Error:', error.response?.status, error.message);
    throw error;
  }
);

export const apiService = {
  // Verificar autenticación con el backend (también actualiza last_login)
  verifyAuth: async () => {
    const response = await apiClient.get('/auth/verify');
    return response.data;
  },

  // Health check
  healthCheck: async () => {
    const response = await apiClient.get('/health');
    return response.data;
  },

  // Obtener países. Opciones: { for_kpis: true } para incluir regiones (ej. LATAm) en dropdown de KPIs
  getCountries: async (options = {}) => {
    const params = {};
    if (options.for_kpis) params.for_kpis = 'true';
    const response = await apiClient.get('/countries', { params });
    return response.data;
  },

  // Obtener canales
  getChannels: async () => {
    const response = await apiClient.get('/channels');
    return response.data;
  },

  // Crear nuevo canal
  createChannel: async (data) => {
    const response = await apiClient.post('/channels', data);
    return response.data;
  },

  // Actualizar canal existente
  updateChannel: async (channelId, data) => {
    const response = await apiClient.put(`/channels/${channelId}`, data);
    return response.data;
  },

  // Eliminar canal
  deleteChannel: async (channelId) => {
    const response = await apiClient.delete(`/channels/${channelId}`);
    return response.data;
  },

  // Obtener campañas
  getCampaigns: async () => {
    const response = await apiClient.get('/campaigns');
    return response.data;
  },

  // Crear nueva campaña
  createCampaign: async (data) => {
    const response = await apiClient.post('/campaigns', data);
    return response.data;
  },

  // Actualizar campaña existente
  updateCampaign: async (campaignId, data) => {
    const response = await apiClient.put(`/campaigns/${campaignId}`, data);
    return response.data;
  },

  // Eliminar campaña (soft delete)
  deleteCampaign: async (campaignId) => {
    const response = await apiClient.delete(`/campaigns/${campaignId}`);
    return response.data;
  },

  // Obtener datos de tracking
  getTrackingData: async (params = {}) => {
    const response = await apiClient.get('/tracking', { params });
    return response.data;
  },

  // Actualizar tracking
  updateTracking: async (data) => {
    const response = await apiClient.post('/tracking', data);
    return response.data;
  },

  // Eliminar todo el tracking de un país en una campaña (al quitar país de la toolbox)
  deleteTrackingByCountryCampaign: async (countryCode, campaignId) => {
    const response = await apiClient.delete('/tracking', {
      params: { country_code: countryCode, campaign_id: campaignId }
    });
    return response.data;
  },

  // Obtener datos del dashboard. Opciones: { debug: true } para incluir _debug en la respuesta (diagnóstico)
  getDashboardData: async (options = {}) => {
    const params = options.debug ? { debug: '1' } : {};
    const response = await apiClient.get('/dashboard', { params });
    return response.data;
  },

  // Obtener canales activos por país
  getCountryChannels: async (countryCode = null) => {
    const params = countryCode ? { country_code: countryCode } : {};
    const response = await apiClient.get('/country-channels', { params });
    return response.data;
  },

  // Activar/desactivar canal en país
  updateCountryChannel: async (data) => {
    const response = await apiClient.post('/country-channels', data);
    return response.data;
  },

  // Obtener canales activos por campaña
  getCampaignChannels: async (campaignId = null) => {
    const params = campaignId ? { campaign_id: campaignId } : {};
    const response = await apiClient.get('/campaign-channels', { params });
    return response.data;
  },

  // Activar/desactivar canal en campaña
  updateCampaignChannel: async (data) => {
    const response = await apiClient.post('/campaign-channels', data);
    return response.data;
  },

  // Obtener KPIs por país-canal-mes
  getChannelKPIs: async (params = {}) => {
    const response = await apiClient.get('/channel-kpis', { params });
    return response.data;
  },

  // Crear/actualizar KPIs
  updateChannelKPIs: async (data) => {
    const response = await apiClient.post('/channel-kpis', data);
    return response.data;
  },
};
